import { SupabaseClient } from '@supabase/supabase-js'
import { resolveAccountsByMappingKeys } from './accountingService'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (supabase: SupabaseClient, t: string): any => (supabase as any).from(t)

// Void-exclusion is done via an embedded inner join to journal_entries rather
// than a two-step "fetch valid ids → .in('journal_id', ids)". The old two-step
// (a) crashed with PGRST100 when the id list was empty, and (b) for large
// tenants built a multi-KB `journal_id=in.(…hundreds of UUIDs…)` URL that
// proxies reject/truncate. `!inner` drops lines whose parent is voided; the
// NULL-safe `not.is.true` treats is_voided IS NULL as not-voided (matches
// COALESCE(is_voided,false)=false — Kyoushi's journals are NULL, not false).
const NOT_VOIDED_EMBED = 'journal_entries!inner(is_voided)'
const applyNotVoided = (q: unknown) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (q as any).not('journal_entries.is_voided', 'is', true)

type Side = 'AR' | 'AP'

// Sum debit/credit per contact over the given accounts, PAGINATED. PostgREST
// caps a single response at ~1000 rows; a tenant with more AR/AP lines than that
// would otherwise be silently truncated and under-report its balances. We page
// with .range(), ordered by contact_id AND the unique primary key `id`: paging by
// contact_id alone is NOT stable, because a contact owns many rows and Postgres
// may order those ties differently between adjacent page requests, splitting a
// contact's lines across the boundary so a line is skipped or double-counted
// (this manufactured a phantom ₦4.165M credit — see pagination.ts). The `id`
// tie-break makes the order total and the paging complete. `sign` picks the
// balance convention: AR is debit-balance (debit − credit), AP is credit-balance
// (credit − debit).
async function sumByContact(
  supabase: SupabaseClient,
  tenantId: string,
  accountIds: string[],
  contactIds: string[] | undefined,
  sign: Side,
): Promise<Map<string, number>> {
  const balanceMap = new Map<string, number>()
  const PAGE = 1000
  for (let from = 0; ; from += PAGE) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query: any = applyNotVoided(
      db(supabase, 'journal_entry_lines')
        .select(`contact_id, debit_amount, credit_amount, ${NOT_VOIDED_EMBED}`)
        .eq('tenant_id', tenantId)
        .in('account_id', accountIds)
        .not('contact_id', 'is', null),
    )
    if (contactIds && contactIds.length > 0) query = query.in('contact_id', contactIds)
    query = query
      .order('contact_id', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1)

    const { data: lines, error } = await query
    if (error) throw error
    const rows = (lines ?? []) as Array<{
      contact_id: string | null
      debit_amount: number | null
      credit_amount: number | null
    }>
    for (const line of rows) {
      if (!line.contact_id) continue
      const d = line.debit_amount ?? 0
      const c = line.credit_amount ?? 0
      const delta = sign === 'AR' ? d - c : c - d
      balanceMap.set(line.contact_id, (balanceMap.get(line.contact_id) ?? 0) + delta)
    }
    if (rows.length < PAGE) break
  }
  return balanceMap
}

export async function getSupplierBalance(
  supabase: SupabaseClient,
  tenantId: string,
  supplierId: string,
): Promise<number> {
  const apIds = await resolveAccountsByMappingKeys(supabase, tenantId, ['accounts_payable', 'payable'])
  if (!apIds.length) return 0
  const map = await sumByContact(supabase, tenantId, apIds, [supplierId], 'AP')
  return map.get(supplierId) ?? 0
}

export async function getCustomerBalance(
  supabase: SupabaseClient,
  tenantId: string,
  customerId: string,
): Promise<number> {
  const arIds = await resolveAccountsByMappingKeys(supabase, tenantId, ['accounts_receivable', 'receivable'])
  if (!arIds.length) return 0
  const map = await sumByContact(supabase, tenantId, arIds, [customerId], 'AR')
  return map.get(customerId) ?? 0
}

// Net journal balance for ONE contact over ALL accounts (debit − credit),
// filtered ONLY by contact_type + contact_id, void-excluded, PAGINATED. This is
// deliberately NOT account-filtered: it is the exact query the post_sale_atomic
// server guard runs (mig 274) to decide available advance, so a reader built on
// it agrees with the guard by construction. Ordered by id for stable paging.
async function contactTypeNet(
  supabase: SupabaseClient,
  tenantId: string,
  contactType: 'customer' | 'supplier',
  contactId: string,
): Promise<number> {
  let net = 0
  const PAGE = 1000
  for (let from = 0; ; from += PAGE) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query: any = applyNotVoided(
      db(supabase, 'journal_entry_lines')
        .select(`id, debit_amount, credit_amount, ${NOT_VOIDED_EMBED}`)
        .eq('tenant_id', tenantId)
        .eq('contact_type', contactType)
        .eq('contact_id', contactId),
    )
    query = query.order('id', { ascending: true }).range(from, from + PAGE - 1)
    const { data: lines, error } = await query
    if (error) throw error
    const rows = (lines ?? []) as Array<{ debit_amount: number | null; credit_amount: number | null }>
    for (const line of rows) net += (line.debit_amount ?? 0) - (line.credit_amount ?? 0)
    if (rows.length < PAGE) break
  }
  return net
}

// Available customer advance (unapplied credit the customer holds), computed
// IDENTICALLY to the post_sale_atomic server guard: net over every journal line
// tagged contact_type='customer' for this customer, void-excluded, then
// GREATEST(0, −net). A net DEBIT (customer owes) → 0 advance. This is the single
// source of truth for the amount a sale/POS checkout may apply as advance — the
// denormalized customers.outstanding_balance column (getCustomerAdvanceBalance)
// must NOT be used for that, as it drifts and can offer a phantom credit the
// server then rejects. Because client and server read the same query, the client
// can never propose more advance than the guard will accept.
export async function getAvailableCustomerAdvance(
  supabase: SupabaseClient,
  tenantId: string,
  customerId: string,
): Promise<number> {
  const net = await contactTypeNet(supabase, tenantId, 'customer', customerId)
  return Math.max(0, -net)
}

export async function getAllSupplierBalances(
  supabase: SupabaseClient,
  tenantId: string,
  supplierIds?: string[],
): Promise<Map<string, number>> {
  if (supplierIds !== undefined && !supplierIds.length) return new Map()
  const apIds = await resolveAccountsByMappingKeys(supabase, tenantId, ['accounts_payable', 'payable'])
  if (!apIds.length) return new Map()
  return sumByContact(supabase, tenantId, apIds, supplierIds, 'AP')
}

export async function getAllCustomerBalances(
  supabase: SupabaseClient,
  tenantId: string,
  customerIds?: string[],
): Promise<Map<string, number>> {
  if (customerIds !== undefined && !customerIds.length) return new Map()
  const arIds = await resolveAccountsByMappingKeys(supabase, tenantId, ['accounts_receivable', 'receivable'])
  if (!arIds.length) return new Map()
  return sumByContact(supabase, tenantId, arIds, customerIds, 'AR')
}
